import { memo, forwardRef, MutableRefObject } from 'react';
import {
  EditorialMiniMenu,
  EditorialMiniMenuProps,
  EditorialMiniMenuConfig,
} from './EditorialMiniMenu';
import {
  QuickFilterMiniMenu,
  QuickFilterMiniMenuConfig,
  QuickFilterMiniMenuProps,
} from './QuickFilterMiniMenu';
import {
  ChannelsMiniMenu,
  ChannelsMiniMenuProps,
  ChannelsMiniMenuConfig,
} from './ChannelsMiniMenu';
import { FeedsMiniMenu, FeedsMiniMenuProps, FeedsMiniMenuConfig } from './FeedsMiniMenu';
import {
  GroupsMiniMenu,
  GroupsMiniMenuProps,
  GroupsMiniMenuConfig,
} from './GroupsMiniMenu';
import {
  DashboardMiniMenu,
  DashboardMiniMenuProps,
  DashboardMiniMenuConfig,
} from 'screens/dashboard/custom-dashboards/components/search/DashboardMiniMenu';
import {
  CampaignMiniMenu,
  CampaignMiniMenuConfig,
} from 'screens/community/emailreach/CampaignsSearch/components/CampaignMiniMenu';
import {
  NotifierMiniMenu,
  NotifierMiniMenuConfig,
} from 'screens/notifier/Search/components/NotifierMiniMenu';
import {
  GenericSearchItem,
  SearchItemsContextType,
} from 'components/Search/components/SearchItemsProvider';
import { GridApiPro } from '@mui/x-data-grid-pro';
import { DASHBOARD_RESOURCE } from 'screens/dashboard/custom-dashboards/utils/constants';

type GridApiRef = MutableRefObject<GridApiPro | null>;
export type RMouseEvent = React.MouseEvent<HTMLElement>;
export type TMenuState = React.MouseEvent<HTMLElement> | null;
export type TSetMenuEvent = (event: RMouseEvent | null) => void;
type SearchItemCtxGeneric = SearchItemsContextType<GenericSearchItem>;
// type SelectedMenuProps = Omit<MenuProps, 'open' | 'onClose'>;

export type MenuConfigMap = {
  editorial?: EditorialMiniMenuConfig;
  dashboards?: DashboardMiniMenuConfig;
  // Quick Filter / Saved Search
  savedsearch?: QuickFilterMiniMenuConfig;
  // Wire Channel
  'wire/channel'?: ChannelsMiniMenuConfig;
  // Wire / Feeds Search
  'wire/channel/search'?: FeedsMiniMenuConfig;
  group?: GroupsMiniMenuConfig;
  // EmailReach Campaign
  'emailreach/campaign'?: CampaignMiniMenuConfig;
  // Notifier Messages
  'broadcast/message'?: NotifierMiniMenuConfig;
};

export type BaseMiniMenuProps = {
  menuEvent: TMenuState; // state ReactMouseEvent | null
  setMenuEvent: TSetMenuEvent; // state (event: ReactMouseEvent | null) => void;
  selectedItems: SearchItemCtxGeneric['selectedItems']; // AnyWithId[] or <T>[]
  setSelectedItems: SearchItemCtxGeneric['setSelectedItems']; // SetStateAction<AnyWithId[]>>
  handleMiniMenuClose: () => void; // onclose callback
  clearSelectedItems: SearchItemCtxGeneric['clearSelectedItems']; // () => void;
  configMap?: MenuConfigMap; // config map for mini menu per type
  raResource?: string; // resource name react admin
  gridApiRef?: GridApiRef; // mouting within mui grid pass api ref
  containerRef?: React.RefObject<HTMLDivElement>; // optional ref for container
  // The item that was right-clicked (not necessarily selected)
  contextMenuItem?: GenericSearchItem | null;
  // True if the context item is disabled (e.g., current asset in modal)
  isContextItemDisabled?: boolean;
  // For button-triggered menu (actions column) — anchors menu to the element
  anchorEl?: HTMLElement | null;
};

export type GenericMiniMenuProps = {
  miniProps: BaseMiniMenuProps;
};

/**
 * Determines which MiniMenu to mount
 * based on the resource type specified
 */
export const MiniMenuSelector = memo(
  forwardRef<HTMLDivElement, GenericMiniMenuProps>(({ miniProps }, ref) => {
    const raResource = miniProps?.raResource;

    switch (raResource) {
      case 'editorial':
      case 'editorial/templates': {
        const config = miniProps?.configMap?.editorial;
        return (
          <EditorialMiniMenu
            ref={ref}
            menuConfig={config}
            {...(miniProps as EditorialMiniMenuProps)}
          />
        );
      }
      case 'savedsearch': {
        const config = miniProps?.configMap?.savedsearch;
        return (
          <QuickFilterMiniMenu
            ref={ref}
            menuConfig={config}
            {...(miniProps as QuickFilterMiniMenuProps)}
          />
        );
      }
      case 'group': {
        const config = miniProps?.configMap?.group;
        return (
          <GroupsMiniMenu
            ref={ref}
            menuConfig={config}
            {...(miniProps as GroupsMiniMenuProps)}
          />
        );
      }
      case 'wire/channel/search': {
        const config = miniProps?.configMap?.['wire/channel/search'];
        return (
          <FeedsMiniMenu
            ref={ref}
            menuConfig={config}
            {...(miniProps as FeedsMiniMenuProps)}
          />
        );
      }
      case 'wire/channel': {
        return (
          <ChannelsMiniMenu
            ref={ref}
            {...(miniProps as ChannelsMiniMenuProps)}
          />
        );
      }
      case DASHBOARD_RESOURCE: {
        const config = miniProps?.configMap?.dashboards;
        return (
          <DashboardMiniMenu
            ref={ref}
            menuConfig={config}
            {...(miniProps as DashboardMiniMenuProps)}
          />
        );
      }
      case 'emailreach/campaign': {
        const config = miniProps?.configMap?.['emailreach/campaign'];
        return (
          <CampaignMiniMenu
            ref={ref}
            menuConfig={config}
            {...miniProps}
          />
        );
      }
      case 'broadcast/message': {
        const config = miniProps?.configMap?.['broadcast/message'];
        return (
          <NotifierMiniMenu
            ref={ref}
            menuConfig={config}
            {...miniProps}
          />
        );
      }
      default: {
        return null;
      }
    }
  }),
);

MiniMenuSelector.displayName = 'MiniMenuSelector';
